import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { ProjectConfigs } from '@completion/enums';
import { ProjectConfig } from '@completion/models';
import { getCurrentConfig, getCurrentProject } from '@completion/selectors';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { ProjectConfigCategory, ProjectConfigMeta, getProjectConfigsByCategory } from 'src/app/shared/enums/project-config';

@Component({
  selector: 'app-project-config',
  templateUrl: './project-config.component.html',
  styleUrls: ['./project-config.component.scss'],
  standalone: false,
})
export class ProjectConfigComponent implements OnInit, OnDestroy {
  config: ProjectConfig[] = [];
  @Input() projectId: number;
  private readonly TRUE = 'true';
  private readonly FALSE = 'false';

  selectedOptions: string[] = [];
  mergedConfig: ProjectConfig[] = [];

  // New: grouped view model for template: [category, { meta, config }] pairs
  groupedConfig: Array<{
    category: ProjectConfigCategory;
    items: Array<{ meta: ProjectConfigMeta; config: ProjectConfig }>;
  }> = [];

  private readonly destroy$ = new Subject<void>();

  constructor(private readonly store: Store) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // Read config from the current project (always populated from API)
    this.store
      .select(getCurrentProject)
      .pipe(takeUntil(this.destroy$))
      .subscribe((project) => {
        console.log('[ProjectConfigComponent] getCurrentProject:', project?.name, 'config count:', project?.config?.length);
        if (project?.config?.length) {
          this.config = project.config;
          this.buildMergedConfig();
          this.buildGroupedConfig();
          console.log('[ProjectConfigComponent] groupedConfig built from project:', this.groupedConfig.length, 'groups');
        }
      });

    // Also react to config updates (dispatched after ProjectConfigUpdate succeeds)
    this.store
      .select(getCurrentConfig)
      .pipe(takeUntil(this.destroy$))
      .subscribe((cfg) => {
        console.log('[ProjectConfigComponent] getCurrentConfig:', cfg?.length);
        if (cfg?.length) {
          this.config = cfg;
          this.buildMergedConfig();
          this.buildGroupedConfig();
          console.log('[ProjectConfigComponent] groupedConfig built from config:', this.groupedConfig.length, 'groups');
        }
      });
  }

  private buildMergedConfig(): void {
    this.mergedConfig = [];
    this.selectedOptions = [];

    const currentByName = new Map<string, ProjectConfig>();
    this.config.forEach((c) => currentByName.set(c.name, c));

    for (const configKey in ProjectConfigs) {
      let value = this.FALSE;
      const existing = currentByName.get(configKey);
      if (existing) {
        value = existing.value;
        if (value === this.TRUE) {
          this.selectedOptions.push(configKey);
        }
      }

      this.mergedConfig.push({
        name: configKey,
        projectId: this.projectId,
        value,
        description: ProjectConfigs[configKey],
      } as ProjectConfig);
    }
  }

  private buildGroupedConfig(): void {
    const byName = new Map<string, ProjectConfig>();
    this.mergedConfig.forEach((cfg) => byName.set(cfg.name, cfg));

    const groupedMap = getProjectConfigsByCategory();
    const result: Array<{
      category: ProjectConfigCategory;
      items: Array<{ meta: ProjectConfigMeta; config: ProjectConfig }>;
    }> = [];

    groupedMap.forEach((metas, category) => {
      const items: Array<{ meta: ProjectConfigMeta; config: ProjectConfig }> = [];
      metas.forEach((meta) => {
        const cfg = byName.get(meta.key); // key is now enum key string, matches cfg.name
        if (cfg) {
          items.push({ meta, config: cfg });
        }
      });
      if (items.length > 0) {
        result.push({ category, items });
      }
    });

    this.groupedConfig = result;
  }
}
